import { useState, useMemo, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useDashboard,
  useCustomerAnalytics,
  useProductAnalytics,
} from "@/hooks/use-analytics";
import { useOrders } from "@/hooks/use-orders";
import { useSession } from "@/hooks/use-auth";
import { useActiveStore } from "@/hooks/use-active-store";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatCurrency, calcDelta } from "@/lib/utils";
import { DeltaBadge } from "@/components/ui/delta-badge";
import {
  ArrowDown,
  ArrowUp,
  CalendarIcon,
  ChevronRight,
  Package,
  RefreshCw,
  TriangleAlert,
  Truck,
  Users,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import { format } from "date-fns";
import { bs } from "date-fns/locale";
import type { DateRange as DayPickerRange } from "react-day-picker";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  getDateRange,
  customGranularity,
  type Period,
  type Granularity,
  type CustomRange,
} from "@/lib/dashboard-range";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const PERIODS: { value: Period; label: string; granularity: Granularity }[] = [
  { value: "today", label: "Danas", granularity: "hour" },
  { value: "yesterday", label: "Jučer", granularity: "hour" },
  { value: "7d", label: "7 dana", granularity: "day" },
  { value: "30d", label: "30 dana", granularity: "day" },
  { value: "year", label: "Ova godina", granularity: "month" },
];

function formatCompact(value: number): string {
  if (Math.abs(value) >= 1000) {
    return (
      new Intl.NumberFormat("bs-BA", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      }).format(value / 1000) + "k"
    );
  }
  return String(Math.round(value));
}

// Bucket ključevi su u GMT jer plugin grupiše po date_created_gmt.
function bucketKeyFromMysql(dateStr: string, gran: Granularity): string {
  if (gran === "hour") return dateStr.slice(0, 13);
  if (gran === "day") return dateStr.slice(0, 10);
  return dateStr.slice(0, 7);
}

function bucketKeyFromDate(d: Date, gran: Granularity): string {
  const iso = d.toISOString();
  if (gran === "hour") return iso.slice(0, 13).replace("T", " ");
  if (gran === "day") return iso.slice(0, 10);
  return iso.slice(0, 7);
}

function bucketLabel(d: Date, gran: Granularity): string {
  if (gran === "hour") return `${String(d.getHours()).padStart(2, "0")}h`;
  if (gran === "day") {
    return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.`;
  }
  return d.toLocaleDateString("bs-BA", { month: "short" });
}

type SeriesPoint = { key: string; label: string; revenue: number; orders: number };

function buildSeries(
  afterISO: string,
  beforeISO: string,
  gran: Granularity,
  intervals: { date_start: string; subtotals: { net_revenue: number; orders_count: number } }[] | undefined
): SeriesPoint[] {
  const map = new Map<string, { revenue: number; orders: number }>();
  for (const iv of intervals || []) {
    const k = bucketKeyFromMysql(iv.date_start, gran);
    const acc = map.get(k) || { revenue: 0, orders: 0 };
    acc.revenue += Number(iv.subtotals.net_revenue) || 0;
    acc.orders += Number(iv.subtotals.orders_count) || 0;
    map.set(k, acc);
  }

  const start = new Date(afterISO);
  const end = new Date(beforeISO);
  let cur: Date;
  if (gran === "hour") cur = new Date(Math.floor(start.getTime() / 3600000) * 3600000);
  else if (gran === "day") cur = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
  else cur = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));

  const out: SeriesPoint[] = [];
  let guard = 0;
  while (cur <= end && guard++ < 500) {
    const k = bucketKeyFromDate(cur, gran);
    const v = map.get(k);
    out.push({ key: k, label: bucketLabel(cur, gran), revenue: v?.revenue ?? 0, orders: v?.orders ?? 0 });
    if (gran === "hour") cur = new Date(cur.getTime() + 3600000);
    else if (gran === "day") cur = new Date(cur.getTime() + 86400000);
    else cur = new Date(Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth() + 1, 1));
  }
  return out;
}

function HeroDelta({ delta }: { delta: number | null }) {
  if (delta === null || delta === 0) return null;
  const positive = delta > 0;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 text-xs font-medium tabular-nums",
        positive ? "text-green-600" : "text-red-500"
      )}
    >
      {positive ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
      {Math.abs(delta).toFixed(1)}%
    </span>
  );
}

function ChartTooltip({ active, payload, label, compare }: any) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="bg-popover ring-1 ring-foreground/15 px-2.5 py-2 text-[10px] shadow-sm">
      <div className="text-muted-foreground mb-1">{label}</div>
      <div className="flex items-center gap-1.5 tabular-nums">
        <span className="inline-block w-3 h-[2px] bg-foreground shrink-0" />
        <span>
          {formatCurrency(row.revenue ?? 0, "KM")} · {row.orders ?? 0} nar.
        </span>
      </div>
      {compare && (
        <div className="flex items-center gap-1.5 tabular-nums text-muted-foreground mt-0.5">
          <span className="inline-block w-3 border-t border-dashed border-muted-foreground shrink-0" />
          <span>
            {row.prevLabel ? `${row.prevLabel} · ` : ""}
            {formatCurrency(row.prevRevenue ?? 0, "KM")}
          </span>
        </div>
      )}
    </div>
  );
}

function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn("text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium", className)}>
      {children}
    </span>
  );
}

export function DashboardPage() {
  const { storeId } = useActiveStore();
  const { user } = useSession();
  const qc = useQueryClient();
  const [period, setPeriod] = useState<Period>("today");
  const [compare, setCompare] = useState(true);
  const [rangeNonce, setRangeNonce] = useState(0);
  const [customRange, setCustomRange] = useState<CustomRange | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingRange, setPendingRange] = useState<DayPickerRange | undefined>(undefined);

  const gran: Granularity =
    period === "custom" && customRange
      ? customGranularity(customRange)
      : (PERIODS.find((p) => p.value === period)?.granularity ?? "day");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const range = useMemo(() => getDateRange(period, customRange), [period, customRange, rangeNonce]);

  const applyCustomRange = (from: Date, to: Date) => {
    setCustomRange({ from, to });
    setPeriod("custom");
    setPickerOpen(false);
  };

  const handleRangeSelect = (r: DayPickerRange | undefined) => {
    setPendingRange(r);
    if (r?.from && r?.to && r.from.getTime() !== r.to.getTime()) {
      applyCustomRange(r.from, r.to);
    }
  };

  const dashboardQ = useDashboard(storeId, range.after, range.before, gran);
  const prevDashboardQ = useDashboard(compare ? storeId : null, range.prevAfter, range.prevBefore, gran);
  const customersQ = useCustomerAnalytics(storeId, range.after, range.before, true);
  const prevCustomersQ = useCustomerAnalytics(storeId, range.prevAfter, range.prevBefore, true);
  const productsQ = useProductAnalytics(storeId, range.after, range.before, true);
  const undeliveredQ = useOrders(storeId, { status: "processing", per_page: "1" });

  const refreshing =
    dashboardQ.isFetching || customersQ.isFetching || productsQ.isFetching || prevDashboardQ.isFetching;

  const refresh = () => {
    setRangeNonce((n) => n + 1);
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["analytics"] });
    qc.invalidateQueries({ queryKey: ["orders"] });
  };

  const chartData = useMemo(() => {
    const cur = buildSeries(range.after, range.before, gran, dashboardQ.data?.intervals);
    if (!compare) return cur;
    const prev = buildSeries(range.prevAfter, range.prevBefore, gran, prevDashboardQ.data?.intervals);
    return cur.map((c, i) => ({
      ...c,
      prevRevenue: prev[i]?.revenue ?? 0,
      prevLabel: prev[i]?.label !== c.label ? prev[i]?.label : undefined,
    }));
  }, [range, gran, compare, dashboardQ.data, prevDashboardQ.data]);

  if (!storeId) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">
          Dodajte trgovinu u <Link to="/stores" className="underline">postavkama trgovina</Link>
        </p>
      </div>
    );
  }

  const totals = dashboardQ.data?.totals;
  const prevTotals = dashboardQ.data?.prev_totals;
  const isLoading = dashboardQ.isLoading;

  const custSummary = customersQ.data?.summary;
  const prevCustSummary = prevCustomersQ.data?.summary;
  const returnRate =
    custSummary && custSummary.total_customers > 0
      ? (custSummary.returning_customers / custSummary.total_customers) * 100
      : null;
  const prevReturnRate =
    prevCustSummary && prevCustSummary.total_customers > 0
      ? (prevCustSummary.returning_customers / prevCustSummary.total_customers) * 100
      : null;

  const velocityById = new Map((productsQ.data?.stock_velocity || []).map((v) => [v.id, v]));
  const topSellers = [...(productsQ.data?.top_sellers || [])]
    .sort((a, b) => Number(b.revenue) - Number(a.revenue))
    .slice(0, 10);
  const maxTopRevenue = Math.max(...topSellers.map((p) => Number(p.revenue) || 0), 1);
  const lowStockCount = (productsQ.data?.stock_velocity || []).filter(
    (v) => v.stock > 0 && v.weeks_until_out > 0 && v.weeks_until_out <= 2
  ).length;
  const undeliveredCount: number | null =
    typeof undeliveredQ.data?.total === "number" ? undeliveredQ.data.total : null;

  const firstName = (user?.name || "").split(" ")[0] || null;

  const kpis = [
    {
      label: "Neto prodaja",
      value: totals ? formatCurrency(totals.net_revenue, "KM") : "—",
      prev: prevTotals ? formatCurrency(prevTotals.net_revenue, "KM") : null,
      delta: totals && prevTotals ? calcDelta(totals.net_revenue, prevTotals.net_revenue) : null,
    },
    {
      label: "Narudžbe",
      value: totals ? String(totals.orders_count) : "—",
      prev: prevTotals ? String(prevTotals.orders_count) : null,
      delta: totals && prevTotals ? calcDelta(totals.orders_count, prevTotals.orders_count) : null,
    },
    {
      label: "Prosj. narudžba",
      value: totals ? formatCurrency(totals.avg_order_value, "KM") : "—",
      prev: prevTotals ? formatCurrency(prevTotals.avg_order_value, "KM") : null,
      delta: totals && prevTotals ? calcDelta(totals.avg_order_value, prevTotals.avg_order_value) : null,
    },
    {
      label: "Artikala prodano",
      value: totals ? String(totals.items_sold) : "—",
      prev: prevTotals ? String(prevTotals.items_sold) : null,
      delta: totals && prevTotals ? calcDelta(totals.items_sold, prevTotals.items_sold) : null,
    },
    {
      label: "Kupci",
      value: totals ? String(totals.total_customers) : "—",
      prev: prevTotals ? String(prevTotals.total_customers) : null,
      delta: totals && prevTotals ? calcDelta(totals.total_customers, prevTotals.total_customers) : null,
    },
    {
      label: "Stopa povratnika",
      value: returnRate !== null ? `${returnRate.toFixed(1)}%` : "—",
      prev: prevReturnRate !== null ? `${prevReturnRate.toFixed(1)}%` : null,
      delta: returnRate !== null && prevReturnRate !== null ? calcDelta(returnRate, prevReturnRate) : null,
    },
  ];

  return (
    <div className="p-4 flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <h1 className="text-lg font-semibold leading-tight">
            {firstName ? `Zdravo, ${firstName}` : "Pregled"}
          </h1>
          <p className="text-muted-foreground text-xs">Evo statistike tvoje prodavnice</p>
        </div>
        <button
          type="button"
          onClick={refresh}
          title="Osvježi podatke"
          className="p-2 ring-1 ring-foreground/10 hover:bg-muted/40 transition-colors text-muted-foreground hover:text-foreground"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", refreshing && "animate-spin")} />
        </button>
      </div>

      {/* Period + Compare */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setPeriod(p.value)}
              className={cn(
                "px-3 py-1.5 text-xs font-medium ring-1 transition-colors",
                period === p.value
                  ? "bg-foreground text-background ring-foreground"
                  : "ring-foreground/10 text-muted-foreground hover:text-foreground hover:bg-muted/40"
              )}
            >
              {p.label}
            </button>
          ))}
          <Popover
            open={pickerOpen}
            onOpenChange={(open) => {
              setPickerOpen(open);
              if (open) {
                setPendingRange(
                  customRange ? { from: customRange.from, to: customRange.to } : undefined
                );
              }
            }}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium ring-1 transition-colors",
                  period === "custom"
                    ? "bg-foreground text-background ring-foreground"
                    : "ring-foreground/10 text-muted-foreground hover:text-foreground hover:bg-muted/40"
                )}
              >
                <CalendarIcon className="h-3 w-3" />
                {period === "custom" && customRange
                  ? `${format(customRange.from, "dd.MM.", { locale: bs })} – ${format(customRange.to, "dd.MM.yyyy", { locale: bs })}`
                  : "Prilagođeno"}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="range"
                numberOfMonths={2}
                locale={bs}
                selected={pendingRange}
                onSelect={handleRangeSelect}
                defaultMonth={customRange?.from}
                disabled={{ after: new Date() }}
              />
              <div className="flex items-center justify-between gap-2 border-t px-3 py-2">
                <span className="text-[10px] text-muted-foreground tabular-nums">
                  {pendingRange?.from
                    ? `${format(pendingRange.from, "dd.MM.yyyy", { locale: bs })} – ${pendingRange.to ? format(pendingRange.to, "dd.MM.yyyy", { locale: bs }) : "…"}`
                    : "Odaberi raspon datuma"}
                </span>
                <button
                  type="button"
                  disabled={!pendingRange?.from}
                  onClick={() => {
                    if (pendingRange?.from) {
                      applyCustomRange(pendingRange.from, pendingRange.to ?? pendingRange.from);
                    }
                  }}
                  className="px-2.5 py-1 text-xs font-medium bg-foreground text-background disabled:opacity-40 transition-opacity"
                >
                  Primijeni
                </button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <button
          type="button"
          onClick={() => setCompare((c) => !c)}
          className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <span
            className={cn(
              "inline-flex h-3 w-3 items-center justify-center ring-1 ring-foreground/30",
              compare && "bg-foreground text-background"
            )}
          >
            {compare && <span className="text-[8px] leading-none">✓</span>}
          </span>
          Uporedi s prethodnim
        </button>
      </div>

      {dashboardQ.error && (
        <p className="text-destructive text-xs">{(dashboardQ.error as Error).message}</p>
      )}

      {/* Hero: ukupna prodaja + graf */}
      {isLoading ? (
        <Skeleton className="h-72 w-full" />
      ) : (
        <div className="ring-1 ring-foreground/10 p-4 flex flex-col gap-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="flex flex-col gap-1">
              <Eyebrow>Ukupna prodaja</Eyebrow>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl sm:text-4xl font-semibold leading-none">
                  {totals ? formatCurrency(totals.total_sales, "KM") : "—"}
                </span>
                <HeroDelta
                  delta={totals && prevTotals ? calcDelta(totals.total_sales, prevTotals.total_sales) : null}
                />
              </div>
              {prevTotals && (
                <span className="text-[10px] text-muted-foreground tabular-nums">
                  Prethodni period: {formatCurrency(prevTotals.total_sales, "KM")}
                </span>
              )}
            </div>
            {compare && (
              <div className="flex gap-3 text-[10px] text-muted-foreground pt-1">
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-3.5 h-[2px] bg-foreground" />
                  Ovaj period
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-3.5 border-t border-dashed border-muted-foreground" />
                  Prethodni
                </span>
              </div>
            )}
          </div>
          <div className="h-48 -ml-1">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <CartesianGrid vertical={false} stroke="var(--border)" />
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  minTickGap={24}
                  tick={{ fontSize: 9, fill: "var(--muted-foreground)" }}
                />
                <YAxis
                  width={36}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={formatCompact}
                  tick={{ fontSize: 9, fill: "var(--muted-foreground)" }}
                />
                <Tooltip
                  content={<ChartTooltip compare={compare} />}
                  cursor={{ stroke: "var(--muted-foreground)", strokeOpacity: 0.4 }}
                />
                {compare && (
                  <Area
                    type="monotone"
                    dataKey="prevRevenue"
                    stroke="var(--muted-foreground)"
                    strokeWidth={1.5}
                    strokeDasharray="4 3"
                    fill="transparent"
                    isAnimationActive={false}
                    dot={false}
                  />
                )}
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="var(--foreground)"
                  strokeWidth={2}
                  fill="var(--foreground)"
                  fillOpacity={0.07}
                  isAnimationActive={false}
                  dot={chartData.length === 1}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* KPI red */}
      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {kpis.map((kpi) => (
            <div key={kpi.label} className="ring-1 ring-foreground/10 p-3 flex flex-col gap-1.5">
              <Eyebrow className="truncate tracking-[0.1em]">{kpi.label}</Eyebrow>
              <div className="flex items-baseline gap-1.5 flex-wrap">
                <span className="text-sm font-semibold tabular-nums">{kpi.value}</span>
                <DeltaBadge delta={kpi.delta} />
              </div>
              {kpi.prev && (
                <span className="text-[10px] text-muted-foreground tabular-nums">Preth: {kpi.prev}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Kupci + Zahtijeva pažnju */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Kupci */}
        <div className="ring-1 ring-foreground/10 flex flex-col">
          <div className="px-3 py-2.5 border-b flex items-center justify-between">
            <span className="flex items-center gap-1.5">
              <Users className="h-3 w-3 text-muted-foreground" />
              <Eyebrow>Kupci</Eyebrow>
            </span>
            <span className="text-[10px] text-muted-foreground tabular-nums">
              {custSummary ? `${custSummary.total_customers} ukupno` : ""}
            </span>
          </div>
          {customersQ.isLoading ? (
            <div className="p-3">
              <Skeleton className="h-20 w-full" />
            </div>
          ) : custSummary ? (
            <div className="p-3 flex flex-col gap-3">
              <div className="flex gap-[2px] h-2">
                <div
                  className="bg-foreground/80"
                  style={{
                    width: `${custSummary.total_customers > 0 ? (custSummary.new_customers / custSummary.total_customers) * 100 : 0}%`,
                  }}
                />
                <div className="bg-foreground/25 flex-1" />
              </div>
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2 text-xs">
                  <span className="h-2 w-2 bg-foreground/80 shrink-0" />
                  <span className="flex-1">Novi</span>
                  <DeltaBadge
                    delta={
                      prevCustSummary
                        ? calcDelta(custSummary.new_customers, prevCustSummary.new_customers)
                        : null
                    }
                  />
                  <span className="font-semibold tabular-nums">{custSummary.new_customers}</span>
                  <span className="text-muted-foreground tabular-nums w-12 text-right">
                    {custSummary.total_customers > 0
                      ? ((custSummary.new_customers / custSummary.total_customers) * 100).toFixed(1)
                      : "0.0"}
                    %
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="h-2 w-2 bg-foreground/25 shrink-0" />
                  <span className="flex-1">Povratnici</span>
                  <DeltaBadge
                    delta={
                      prevCustSummary
                        ? calcDelta(custSummary.returning_customers, prevCustSummary.returning_customers)
                        : null
                    }
                  />
                  <span className="font-semibold tabular-nums">{custSummary.returning_customers}</span>
                  <span className="text-muted-foreground tabular-nums w-12 text-right">
                    {returnRate !== null ? returnRate.toFixed(1) : "0.0"}%
                  </span>
                </div>
              </div>
              {prevCustSummary && prevCustSummary.total_customers > 0 && (
                <span className="text-[10px] text-muted-foreground tabular-nums">
                  Prethodno: {prevCustSummary.total_customers} kupaca ·{" "}
                  {((prevCustSummary.new_customers / prevCustSummary.total_customers) * 100).toFixed(0)}% novih
                </span>
              )}
            </div>
          ) : (
            <p className="p-3 text-xs text-muted-foreground">Nema podataka za odabrani period.</p>
          )}
        </div>

        {/* Zahtijeva pažnju */}
        <div className="ring-1 ring-foreground/10 flex flex-col">
          <div className="px-3 py-2.5 border-b">
            <Eyebrow>Zahtijeva pažnju</Eyebrow>
          </div>
          <div className="divide-y flex-1">
            <Link
              to="/orders"
              className="flex items-center justify-between gap-2 px-3 py-3 hover:bg-muted/30 transition-colors"
            >
              <span className="flex items-center gap-2 text-xs">
                <Truck className="h-3.5 w-3.5 text-muted-foreground" />
                Neisporučene narudžbe
              </span>
              <span className="flex items-center gap-1.5">
                <span
                  className={cn(
                    "tabular-nums text-xs px-1.5 py-0.5 ring-1 ring-foreground/10",
                    undeliveredCount !== null && undeliveredCount > 0 && "text-destructive ring-destructive/30 font-semibold"
                  )}
                >
                  {undeliveredCount ?? "—"}
                </span>
                <ChevronRight className="h-3 w-3 text-muted-foreground" />
              </span>
            </Link>
            <Link
              to="/analytics"
              className="flex items-center justify-between gap-2 px-3 py-3 hover:bg-muted/30 transition-colors"
            >
              <span className="flex items-center gap-2 text-xs">
                <TriangleAlert className="h-3.5 w-3.5 text-muted-foreground" />
                Zalihe pri kraju (&le; 2 sedmice)
              </span>
              <span className="flex items-center gap-1.5">
                <span
                  className={cn(
                    "tabular-nums text-xs px-1.5 py-0.5 ring-1 ring-foreground/10",
                    lowStockCount > 0 && "text-destructive ring-destructive/30 font-semibold"
                  )}
                >
                  {productsQ.isLoading ? "—" : lowStockCount}
                </span>
                <ChevronRight className="h-3 w-3 text-muted-foreground" />
              </span>
            </Link>
          </div>
        </div>
      </div>

      {/* Top proizvodi */}
      <div className="ring-1 ring-foreground/10">
        <div className="px-3 py-2.5 border-b flex items-center gap-1.5">
          <Package className="h-3 w-3 text-muted-foreground" />
          <Eyebrow>Top proizvodi</Eyebrow>
        </div>
        {productsQ.isLoading ? (
          <div className="p-3">
            <Skeleton className="h-48 w-full" />
          </div>
        ) : topSellers.length > 0 ? (
          <div className="divide-y">
            {topSellers.map((product, i) => {
              const revenue = Number(product.revenue) || 0;
              const pct = (revenue / maxTopRevenue) * 100;
              const velocity = velocityById.get(product.id);
              const daysLeft =
                velocity && velocity.weeks_until_out > 0 ? Math.round(velocity.weeks_until_out * 7) : null;
              const perDay = product.items_sold / range.days;
              return (
                <div key={`${product.id}-${i}`} className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/30 transition-colors">
                  <span className="text-[10px] text-muted-foreground w-4 shrink-0 tabular-nums text-right">
                    {i + 1}
                  </span>
                  {product.image ? (
                    <img
                      src={product.image}
                      alt=""
                      loading="lazy"
                      className="h-10 w-10 object-cover bg-muted shrink-0"
                    />
                  ) : (
                    <span className="h-10 w-10 bg-muted shrink-0 flex items-center justify-center">
                      <Package className="h-4 w-4 text-muted-foreground" />
                    </span>
                  )}
                  <div className="flex-1 min-w-0 flex flex-col gap-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-xs font-medium truncate">{product.name || "—"}</span>
                      <span className="text-xs font-semibold tabular-nums shrink-0">
                        {formatCurrency(revenue, "KM")}
                      </span>
                    </div>
                    <div className="h-1.5 bg-muted">
                      <div className="h-full bg-foreground/70" style={{ width: `${Math.max(pct, 2)}%` }} />
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground tabular-nums flex-wrap">
                      <span>{product.items_sold} prodano</span>
                      <span>·</span>
                      <span>~{perDay >= 10 ? Math.round(perDay) : perDay.toFixed(1)}/dan</span>
                      {velocity && (
                        <>
                          <span>·</span>
                          <span>zaliha {velocity.stock}</span>
                          {velocity.stock <= 0 ? (
                            <span className="text-destructive font-medium">rasprodano</span>
                          ) : daysLeft !== null ? (
                            <span className={cn(daysLeft <= 7 && "text-destructive font-medium")}>
                              {daysLeft <= 7 ? "pri kraju · " : ""}još ~{daysLeft} d
                            </span>
                          ) : null}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="p-3 text-xs text-muted-foreground">Nema prodaja u odabranom periodu.</p>
        )}
      </div>
    </div>
  );
}
